import React, { useEffect, useState } from "react";
import { api, inrPlain, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Sparkles, Check, ShieldCheck, ArrowLeft, Receipt, Smartphone, Coins, Landmark, CreditCard, Wallet } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import UpgradeDialog from "@/components/UpgradeDialog";
import { CardSkeleton, ListSkeleton } from "@/components/Skeletons";

const MODE_ICONS = { UPI: Smartphone, Cash: Coins, Bank: Landmark, Card: CreditCard };

export default function Subscription() {
  const { user, refresh: refreshAuth } = useAuth();
  const [plan, setPlan] = useState(null);
  const [txns, setTxns] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [p, t] = await Promise.all([
        api.get("/subscriptions/plan"),
        api.get("/subscriptions/my-transactions"),
      ]);
      setPlan(p.data);
      setTxns(t.data);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const isPro = user?.plan === "pro" || user?.role === "superadmin";
  const isSuper = user?.role === "superadmin";

  return (
    <div className="space-y-5 lg:max-w-3xl" data-testid="subscription-page">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/profile")} className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center" data-testid="subscription-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Billing</div>
          <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Subscription</h1>
        </div>
      </div>

      {/* Plan card */}
      {!plan ? (
        <CardSkeleton />
      ) : (
        <div className={`rounded-3xl p-7 border grain relative overflow-hidden ${
          isSuper ? "bg-slate-900 text-white border-slate-800" :
          isPro ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"
        }`} data-testid="subscription-plan-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className={`text-xs uppercase tracking-widest font-semibold ${isSuper ? "text-emerald-300" : "text-slate-500"}`}>
                Current plan
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className={`amount text-4xl font-bold tracking-tight ${isSuper ? "text-white" : isPro ? "text-emerald-700" : "text-slate-900"}`} data-testid="subscription-current-plan">
                  {isSuper ? "Super admin" : isPro ? "Pro" : "Free"}
                </span>
                {!isSuper && (
                  <span className={`text-xs uppercase tracking-widest font-bold px-2 py-1 rounded-full ${isPro ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                    {isPro ? "ACTIVE" : "STARTER"}
                  </span>
                )}
              </div>
              {isPro && !isSuper && user?.plan_expires_at && (
                <div className="text-sm text-slate-600 mt-2" data-testid="subscription-expiry">
                  Valid until {format(new Date(user.plan_expires_at), "PPP")}
                </div>
              )}
              {isSuper && (
                <div className="text-sm text-slate-300 mt-2">Full access — no plan limits.</div>
              )}
            </div>
            {isSuper ? (
              <ShieldCheck className="w-12 h-12 text-emerald-300 opacity-70" />
            ) : (
              <Sparkles className={`w-12 h-12 ${isPro ? "text-emerald-400" : "text-slate-300"} opacity-70`} />
            )}
          </div>

          {/* Feature list + upgrade */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            <FeatureBullet label="Unlimited members per group" enabled />
            <FeatureBullet label={isPro ? "Unlimited groups" : "1 group max"} enabled={isPro} />
            <FeatureBullet label="Immutable ledger + reports" enabled />
            <FeatureBullet label="Priority support" enabled={isPro} />
          </div>

          {!isPro && (
            <Button
              onClick={() => setUpgradeOpen(true)}
              className="mt-6 w-full md:w-auto h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6"
              data-testid="subscription-upgrade-button"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Upgrade to Pro
              {plan && <span className="ml-2 opacity-70">· ₹{plan.price_inr} / {plan.duration_days}d</span>}
            </Button>
          )}
        </div>
      )}

      {/* Transactions */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="w-4 h-4 text-slate-400" />
          <h2 className="heading text-lg font-semibold text-slate-900">Transaction history</h2>
        </div>
        {!txns ? (
          <ListSkeleton rows={2} />
        ) : txns.length === 0 ? (
          <div className="p-6 rounded-2xl bg-white border border-slate-200 text-center text-sm text-slate-500" data-testid="subscription-no-txns">
            <Wallet className="w-8 h-8 text-slate-300 mx-auto" />
            <p className="mt-3">No transactions yet.</p>
            {!isPro && <p className="text-xs mt-1">Upgrade to Pro to see your first payment here.</p>}
          </div>
        ) : (
          <div className="space-y-2">
            {txns.map((t) => {
              const statusColor = t.status === "paid" || t.status === "paid_simulated" ? "bg-emerald-100 text-emerald-700"
                : t.status === "created" ? "bg-slate-100 text-slate-600"
                : "bg-rose-100 text-rose-700";
              return (
                <div key={t.id} className="p-4 rounded-2xl bg-white border border-slate-200" data-testid={`subscription-txn-${t.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-emerald-50 grid place-items-center">
                      <Receipt className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-slate-900">Pro upgrade</div>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${statusColor}`}>
                          {t.status === "paid_simulated" ? "PAID · SIMULATED" : t.status}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1 truncate">
                        {t.created_at && format(new Date(t.created_at), "PPP · p")}
                        {t.razorpay_mode && ` · ${t.razorpay_mode.toUpperCase()} mode`}
                      </div>
                      {(t.order_id || t.payment_id) && (
                        <div className="text-[11px] font-mono text-slate-500 mt-1 truncate">
                          {t.payment_id ? `pmt: ${t.payment_id}` : `ord: ${t.order_id}`}
                        </div>
                      )}
                    </div>
                    <div className="amount font-bold text-slate-900 text-lg">{inrPlain(t.amount_inr)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        onUpgraded={async () => { await refreshAuth?.(); load(); }}
      />
    </div>
  );
}

function FeatureBullet({ label, enabled }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${enabled ? "text-slate-800" : "text-slate-400"}`}>
      <Check className={`w-4 h-4 ${enabled ? "text-emerald-600" : "text-slate-300"}`} />
      <span>{label}</span>
    </div>
  );
}
